/* ============================================================================
   HOUND — SCROLL AFFORDANCE DRIVER
   ============================================================================
   Several RPG columns scroll with the native scrollbar suppressed (a deliberate
   look — the native bar flashed over the parchment quest cards and read as a
   glitch). The side effect was that a clipped list looked identical to a list
   that simply ended, so on a 14" laptop, where the quest column loses ~131px of
   cards below the fold, users never learned there was more to see.

   This module marks any element carrying [data-hound-scroll] with two attributes
   that assets/hound-responsive.css keys off:

     data-scrollable  "1" when scrollHeight exceeds clientHeight, else "0"
     data-at-end      "1" when scrolled to within END_EPS of the bottom

   It deliberately does NOT restore the native scrollbar or add markup — the CSS
   decides how to present the state (a bottom mask fade plus a slim gold rail).

   Re-measures on scroll, on resize, and whenever the subtree changes, because
   these lists are populated asynchronously and swap contents on tab changes.
   ========================================================================== */
(function () {
  'use strict';

  var END_EPS = 4;         // px from the bottom that still counts as "the end"
  var SCROLL_EPS = 2;      // ignore sub-pixel scrollHeight/clientHeight noise
  var registry = new WeakMap();

  function measure(el) {
    var scrollable = el.scrollHeight > el.clientHeight + SCROLL_EPS;
    var atEnd = !scrollable ||
      (el.scrollTop + el.clientHeight >= el.scrollHeight - END_EPS);

    var s = scrollable ? '1' : '0';
    var e = atEnd ? '1' : '0';
    // Only write when the value actually changes — these run on scroll, and a
    // redundant attribute write invalidates style on every frame.
    if (el.getAttribute('data-scrollable') !== s) el.setAttribute('data-scrollable', s);
    if (el.getAttribute('data-at-end') !== e) el.setAttribute('data-at-end', e);
  }

  function attach(el) {
    if (registry.has(el)) return;

    var raf = 0;
    var schedule = function () {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = 0; measure(el); });
    };

    el.addEventListener('scroll', schedule, { passive: true });

    // Content is injected asynchronously (quest lists rebuild on tab switch, the
    // armory fills from an API), so a one-shot measure at init would be wrong.
    var mo = new MutationObserver(schedule);
    mo.observe(el, { childList: true, subtree: true, characterData: true });

    // clientHeight changes with the viewport; scrollHeight changes when webfonts
    // land and reflow the cards. ResizeObserver catches both.
    var ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(schedule);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }

    registry.set(el, { mo: mo, ro: ro });
    measure(el);
  }

  function scan(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-hound-scroll]');
    for (var i = 0; i < nodes.length; i++) attach(nodes[i]);
  }

  function boot() {
    scan(document);
    // Sections mount lazily as the user navigates between RPG views, so keep
    // watching the document for newly inserted scroll ports.
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.hasAttribute && n.hasAttribute('data-hound-scroll')) attach(n);
          if (n.querySelectorAll) scan(n);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });

    // Webfonts reflow the cards after first paint and change scrollHeight.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { scan(document); });
    }
    window.addEventListener('resize', function () { scan(document); }, { passive: true });
    window.addEventListener('orientationchange', function () {
      setTimeout(function () { scan(document); }, 250);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.houndScrollAffordance = { scan: scan, attach: attach };
})();
